import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';
import { ODataQuery } from '../ODataApi';
import { useDashboardFilters } from '../../contexts/DashboardFilterContext';

export interface WidgetDataSource {
  datastore_id?: string;
  table_name?: string;
  query?: string;
  filters?: Record<string, any>;
  api_service_name?: string;
  api_version?: string;
  entity_name?: string;
  // Additional data source configuration
  [key: string]: any;
}

export interface Widget {
  id: string;
  widget_type:
    | 'table'
    | 'chart'
    | 'field_kv'
    | 'control'
    | 'form'
    | 'image'
    | 'map'
    | 'digital_twin'
    | 'ai_chat';
  data_source?: WidgetDataSource;
  configuration?: {
    title?: string;
    [key: string]: any;
  };
}

interface PaginationOptions {
  page: number;
  pageSize: number;
}

interface SortingOptions {
  column: string;
  direction: 'asc' | 'desc';
}

interface UseWidgetDataOptions {
  enabled?: boolean;
  staleTime?: number;
  refetchInterval?: number | false;
  pagination?: PaginationOptions;
  sorting?: SortingOptions;
}

/**
 * Universal hook for fetching widget data from various sources
 */
export function useWidgetData(
  widget: Widget | undefined,
  accountId: string,
  options: UseWidgetDataOptions = {},
) {
  const {
    enabled = true,
    staleTime = 300000, // 5 minutes
    refetchInterval = false,
    pagination,
    sorting,
  } = options;

  // Get global dashboard filters (returns empty object if not in provider)
  const { filters: globalFilters } = useDashboardFilters();

  // Fetch schema to know which fields are available
  const { data: schemaData } = useWidgetDataSchema(
    widget?.data_source?.api_service_name,
    widget?.data_source?.entity_name,
    accountId,
    !!widget?.data_source?.api_service_name &&
      !!widget?.data_source?.entity_name,
  );

  // Get available field names from schema
  const availableFields =
    schemaData && schemaData.length > 0 ? Object.keys(schemaData[0]) : [];

  return useQuery({
    queryKey: [
      'widget-data',
      widget?.id,
      widget?.data_source,
      accountId,
      globalFilters,
      pagination,
      sorting,
    ],
    queryFn: async () => {
      if (!widget?.data_source) {
        throw new Error('No data source configured for widget');
      }

      // Filter global filters to only include fields that exist on this entity
      const applicableGlobalFilters =
        availableFields.length > 0
          ? Object.fromEntries(
              Object.entries(globalFilters).filter(([key]) =>
                availableFields.includes(key),
              ),
            )
          : {}; // If schema not available, skip all global filters (can't verify they exist)

      // Track which filters were skipped
      const skippedFilters =
        availableFields.length > 0
          ? Object.keys(globalFilters).filter(
              (key) => !applicableGlobalFilters[key] && globalFilters[key],
            )
          : Object.keys(globalFilters).filter((key) => globalFilters[key]); // If no schema, all filters are skipped

      // Merge data source filters with applicable global filters
      const mergedFilters = {
        ...widget.data_source.filters,
        ...applicableGlobalFilters,
      };

      const result = await fetchWidgetData(
        {
          ...widget.data_source,
          filters: mergedFilters,
        },
        accountId,
        widget,
        pagination,
        sorting,
      );

      // Attach metadata about skipped filters to the result
      return {
        data: result.data,
        totalCount: result.totalCount,
        skippedFilters,
        availableFields,
      };
    },
    enabled: enabled && !!widget && !!widget.data_source && !!accountId,
    staleTime,
    refetchInterval,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Fetch widget data based on data source configuration
 */
async function fetchWidgetData(
  dataSource: WidgetDataSource,
  accountId: string,
  widget?: Widget,
  pagination?: PaginationOptions,
  sorting?: SortingOptions,
): Promise<{ data: any[]; totalCount?: number }> {
  const {
    datastore_id,
    table_name,
    query,
    filters = {},
    api_service_name,
    api_version,
    entity_name,
  } = dataSource;

  // Priority 1: Datastore discovery (new approach)
  if (datastore_id && entity_name) {
    return await fetchFromDatastoreOData(
      accountId,
      datastore_id,
      entity_name,
      filters,
      pagination,
      sorting,
    );
  }

  // Priority 2: API service OData (existing approach)
  if (api_service_name && api_version && entity_name) {
    return await fetchFromSophiaOData(
      accountId,
      api_service_name,
      api_version,
      entity_name,
      filters,
      pagination,
      sorting,
    );
  }

  // Priority 3: Legacy table name (backward compatibility)
  if (table_name) {
    console.log('🏛️ Using OData for table:', table_name);
    return await fetchFromSophiaOData(
      accountId,
      'dorothy-data-api', // Service name for Dorothy database
      'v1', // API version
      table_name, // Entity name (table/view name)
      filters,
      pagination,
      sorting,
    );
  }

  // DEMO: For widgets with title "Dorothy Analytics Table", use Dorothy OData
  if (
    !table_name &&
    widget?.configuration?.title === 'Dorothy Analytics Table'
  ) {
    console.log('🎯 Demo: Using Dorothy OData for demo widget');
    return await fetchFromSophiaOData(
      accountId,
      'dorothy-data-api',
      'v1',
      'sales_analytics',
      filters,
      pagination,
      sorting,
    );
  }

  // If no OData configuration available, return empty result
  console.warn('⚠️ No OData configuration available for widget');
  return { data: [] };
}

/**
 * Convert dashboard filters to OData $filter query string
 */
function buildODataFilter(filters: Record<string, any>): string {
  const filterParts: string[] = [];

  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return; // Skip empty filters
    }

    // Handle different filter types
    if (Array.isArray(value)) {
      // Multi-select: use OR conditions instead of IN operator
      // Changed from: category in ('Electronics', 'Clothing')
      // To: (category eq 'Electronics' or category eq 'Clothing')
      if (value.length > 0) {
        if (value.length === 1) {
          // Single value: category eq 'Electronics'
          filterParts.push(`${key} eq '${value[0]}'`);
        } else {
          // Multiple values: (category eq 'Electronics' or category eq 'Clothing')
          const orConditions = value
            .map((v) => `${key} eq '${v}'`)
            .join(' or ');
          filterParts.push(`(${orConditions})`);
        }
      }
    } else if (typeof value === 'object' && value.from !== undefined) {
      // Date range filter
      if (value.from && value.to) {
        const fromDate = format(new Date(value.from), 'yyyy-MM-dd');
        const toDate = format(new Date(value.to), 'yyyy-MM-dd');
        filterParts.push(`${key} ge ${fromDate} and ${key} le ${toDate}`);
      } else if (value.from) {
        const fromDate = format(new Date(value.from), 'yyyy-MM-dd');
        filterParts.push(`${key} ge ${fromDate}`);
      } else if (value.to) {
        const toDate = format(new Date(value.to), 'yyyy-MM-dd');
        filterParts.push(`${key} le ${toDate}`);
      }
    } else if (typeof value === 'string') {
      // Text search or exact match
      if (key === 'search') {
        // Global search across all string fields (OData doesn't have a built-in search-all)
        // We can use contains() for common fields
        filterParts.push(
          `(contains(product_name, '${value}') or contains(category, '${value}') or contains(region, '${value}'))`,
        );
      } else {
        // Exact match for specific field
        filterParts.push(`${key} eq '${value}'`);
      }
    } else if (typeof value === 'number') {
      filterParts.push(`${key} eq ${value}`);
    } else if (typeof value === 'boolean') {
      filterParts.push(`${key} eq ${value}`);
    }
  });

  return filterParts.join(' and ');
}

/**
 * Fetch data from datastore discovery OData endpoint
 */
async function fetchFromDatastoreOData(
  accountId: string,
  datastoreId: string,
  entityName: string,
  filters: Record<string, any> = {},
  pagination?: PaginationOptions,
  sorting?: SortingOptions,
): Promise<{ data: any[]; totalCount?: number }> {
  const filterString = buildODataFilter(filters);
  const filterParam = filterString
    ? `&$filter=${encodeURIComponent(filterString)}`
    : '';

  // Build pagination params
  const top = pagination?.pageSize || 1000;
  const skip = pagination ? pagination.page * pagination.pageSize : 0;
  const paginationParams = `$top=${top}&$skip=${skip}&$count=true`;

  // Build sorting params
  const sortParam = sorting
    ? `&$orderby=${sorting.column} ${sorting.direction}`
    : '';

  // Note: Don't include /api/ prefix - ODataQuery adds it automatically
  const url = `/accounts/${accountId}/datastores/${datastoreId}/odata/v1/${entityName}?${paginationParams}${filterParam}${sortParam}`;

  const odataQuery = new ODataQuery(url);
  const result = await odataQuery.run();

  // ODataQuery.run() now returns the full response object (includes @odata.count, value, etc.)
  const data = (result as any)?.value || result || [];
  const totalCount = (result as any)?.['@odata.count'];

  return {
    data,
    totalCount,
  };
}

/**
 * Fetch data from Sophia OData endpoint (API service)
 */
async function fetchFromSophiaOData(
  accountId: string,
  serviceName: string,
  version: string,
  entityName: string,
  filters: Record<string, any> = {},
  pagination?: PaginationOptions,
  sorting?: SortingOptions,
): Promise<{ data: any[]; totalCount?: number }> {
  console.log('📡 Fetching from Sophia OData:', {
    accountId,
    serviceName,
    version,
    entityName,
    filters,
    pagination,
    sorting,
  });

  // Build OData filter string
  const filterString = buildODataFilter(filters);

  // Build query parameters
  const params: string[] = [];

  // Pagination
  const top = pagination?.pageSize || 1000;
  const skip = pagination ? pagination.page * pagination.pageSize : 0;
  params.push(`$top=${top}`);
  params.push(`$skip=${skip}`);
  params.push(`$count=true`);

  // Filtering
  if (filterString) {
    params.push(`$filter=${encodeURIComponent(filterString)}`);
  }

  // Sorting
  if (sorting) {
    params.push(`$orderby=${sorting.column} ${sorting.direction}`);
  }

  // Construct the URL with parameters
  const url = `/accounts/${accountId}/services/${serviceName}/odata/${version}/${entityName}?${params.join('&')}`;

  const odataQuery = new ODataQuery(url);

  // The OData query uses the configured API base URL
  // which routes through nginx to the real Sophia service

  try {
    const result = await odataQuery.run();

    // OData typically returns data in a 'value' array
    const data = Array.isArray(result) ? result : result?.value || [];
    const totalCount = (result as any)?.['@odata.count'];

    return {
      data,
      totalCount,
    };
  } catch (error) {
    console.error('❌ Sophia OData error:', error);
    throw new Error(`Failed to fetch data from Sophia: ${error}`);
  }
}

/**
 * Hook for testing widget data sources
 */
export function useTestWidgetData(
  accountId: string,
  dataSource: WidgetDataSource,
  options: UseWidgetDataOptions = {},
) {
  const testWidget: Widget = {
    id: 'test-widget',
    widget_type: 'table',
    data_source: dataSource,
  };

  return useWidgetData(testWidget, accountId, options);
}

/**
 * Hook for fetching data schema (available fields) from a data source
 * This is used to populate field selectors in configuration UIs
 */
export function useWidgetDataSchema(
  apiServiceName: string | undefined,
  entityName: string | undefined,
  accountId: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['widget-data-schema', apiServiceName, entityName, accountId],
    queryFn: async () => {
      if (!apiServiceName || !entityName) {
        throw new Error('API service name and entity name are required');
      }

      // Fetch a single row to get the schema
      // Use ODataQuery to ensure we use the correct BASE_URL from environment
      const url = `/accounts/${accountId}/services/${apiServiceName}/odata/v1/${entityName}?$top=1`;
      const odataQuery = new ODataQuery(url);

      const result = await odataQuery.run();

      // Return the result (either array or object with 'value' property)
      return Array.isArray(result) ? result : result?.value || [];
    },
    enabled: enabled && !!apiServiceName && !!entityName && !!accountId,
    staleTime: 600000, // 10 minutes - schema doesn't change often
    retry: 1,
  });
}

// Sample data hooks removed - always use real data from backend
